import { createTheme } from '@mui/material/styles';

// Company brand colors
const BRAND_COLORS = {
  yellow: '#FFFF00',  // Company yellow
  black: '#000000',  // Company black
};

export const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-toolpad-color-scheme',
  },
  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: {
          main: '#4682B4',    // Steel blue - Primary color
          light: '#7eaacd',   // Light blue - Hover states
          dark: '#2c5282',    // Dark blue - Pressed states
        },
        secondary: {
          main: BRAND_COLORS.black,     // Black - Secondary color
          light: '#404040',            // Dark gray - Hover states
          dark: '#000000',             // Black - Pressed states
        },
        background: {
          default: '#f5f5f5',          // Light gray - Main background
          paper: '#ffffff',            // White - Card/panel backgrounds
        },
        text: {
          primary: BRAND_COLORS.black, // Black - Primary text
          secondary: 'rgba(0, 0, 0, 0.6)', // Dark gray - Secondary text
        },
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        primary: {
          main: BRAND_COLORS.yellow,   // Yellow - Primary brand color
          light: 'rgba(255, 255, 0, 0.8)', // Light yellow - Hover states
          dark: '#cccc00',             // Dark yellow - Pressed states
        },
        secondary: {
          main: '#4682B4',     // Steel blue - Secondary color
          light: '#7eaacd',            // Light blue - Hover states
          dark: '#2c5282',             // Dark blue - Pressed states
        },
        background: {
          default: '#121212',          // Very dark gray - Main background
          paper: '#1e1e1e',            // Dark gray - Card/panel backgrounds
        },
        text: {
          primary: '#ffffff',          // White - Primary text
          secondary: 'rgba(255, 255, 255, 0.7)', // Light gray - Secondary text
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 600,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 400,
        },
        outlined: {
          '[data-toolpad-color-scheme="light"] &': {
            borderColor: 'text.secondary',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'text.primary',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            '& .MuiSvgIcon-root': {
              color: '#4682B4', // Steel blue for light mode
            },
            '&.Mui-disabled': {
              borderColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
              '& .MuiSvgIcon-root': {
                color: 'rgba(0, 0, 0, 0.26)',
              },
            },
          },
          '[data-toolpad-color-scheme="dark"] &': {
            borderColor: 'rgba(255, 255, 255, 0.7)',
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              borderColor: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
            '& .MuiSvgIcon-root': {
              color: BRAND_COLORS.yellow, // Yellow for dark mode
            },
            '&.Mui-disabled': {
              borderColor: 'rgba(255, 255, 255, 0.12)',
              color: 'rgba(255, 255, 255, 0.26)',
              '& .MuiSvgIcon-root': {
                color: 'rgba(255, 255, 255, 0.26)',
              },
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '40px',
          '[data-toolpad-color-scheme="light"] & .MuiSvgIcon-root': {
            color: '#4682B4', // Steel blue for light mode
          },
          '[data-toolpad-color-scheme="dark"] & .MuiSvgIcon-root': {
            color: BRAND_COLORS.yellow, // Yellow for dark mode
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '[data-toolpad-color-scheme="light"] &': {
            color: '#4682B4', // Steel blue for light mode
          },
          '[data-toolpad-color-scheme="dark"] &': {
            color: BRAND_COLORS.yellow, // Yellow for dark mode
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '[data-toolpad-color-scheme="light"] &': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'text.secondary',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'text.primary',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'text.primary',
            },
          },
          '[data-toolpad-color-scheme="dark"] &': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.7)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ffffff',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ffffff',
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '[data-toolpad-color-scheme="light"] &': {
            color: 'text.secondary',
            '&.Mui-focused': {
              color: 'text.primary',
            },
          },
          '[data-toolpad-color-scheme="dark"] &': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#ffffff',
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          '[data-toolpad-color-scheme="light"] & .MuiSvgIcon-root': {
            color: '#4682B4', // Steel blue for light mode
          },
          '[data-toolpad-color-scheme="dark"] & .MuiSvgIcon-root': {
            color: BRAND_COLORS.yellow, // Yellow for dark mode
          },
        },
      },
    },
  },
}); 